import axios from 'axios';
import { rateLimitMessage, retryAfterMs } from './rate-limit.js';

// TSS vite/04 §"Error shape — ApiError".
// Every error thrown by the api instance is an ApiError.

// THIS BACKEND ANSWERS IN TWO SHAPES, and reading the wrong field of either
// one throws away the sentence a person needs:
//
//   boom  → { statusCode: 403, error: 'Forbidden', message: 'Quien revisa no
//             puede ser quien entregó…' }          the reason phrase is `error`
//   plain → { error: 'CSRF check failed' }         the message IS `error`
//
// The template read `data.error` first, which is right for the second and
// exactly wrong for the first: every domain refusal in the panel came out as
// "Forbidden", "Bad Request" or "Conflict" — the HTTP status spelled twice,
// with the explanation dropped. `statusCode` is what tells the two apart,
// because only boom sends it.
//
// TSS vite/04 says the envelope is `{ error, fields }`; mastermind_core emits
// boom's payload verbatim (middlewares/error/error.js). Where the standard and
// the wire disagree, the wire wins — the panel does not get to be right in
// principle and useless on screen.
function readMessage(data, status) {
    if (!data) return `Error ${status}`;
    if (typeof data === 'string') return data;
    if (typeof data.statusCode === 'number') return data.message || data.error || `Error ${status}`;
    return data.error || data.message || `Error ${status}`;
}

// THE STRUCTURED DETAIL A REFUSAL CARRIES, and why it is read generically.
//
// The breakdown surface answers a refusal with more than a sentence: `gate`
// says WHICH door closed (`revision`, `congelado`, `estado`, `idempotencia`),
// `missing` lists what a tier still needs before it can be agreed, `errors`
// carries one entry per row of a rejected batch, and a stale-revision conflict
// reports the revision the row actually has.
//
// NONE OF THAT IS IN THE PUBLISHED `Error` SCHEMA — components.schemas.Error
// declares statusCode, error, message and fields, and nothing else. The rest is
// documented in prose (api-reference.md) and emitted by the backend. So this
// reads whatever arrived instead of destructuring names the contract does not
// promise: a field that moves shows up as absent, not as a crash, and a field
// that is added arrives without a change here.
const DETAIL_KEYS = ['gate', 'missing', 'errors', 'revision', 'request', 'hidden_dependencies'];

function readDetail(data) {
    if (!data || typeof data !== 'object') return null;
    const detail = {};
    for (const key of DETAIL_KEYS) {
        if (data[key] !== undefined) detail[key] = data[key];
    }
    return Object.keys(detail).length ? detail : null;
}

export class ApiError extends Error {
    constructor({ status, message, code, fields, detail, isNetwork, isTimeout, cause, retryAfter = 0 }) {
        super(message);
        this.name = 'ApiError';
        this.status = status ?? null;
        this.code = code ?? null;
        this.fields = fields ?? null;
        this.detail = detail ?? null;
        this.isNetwork = !!isNetwork;
        this.isTimeout = !!isTimeout;
        this.cause = cause;
        this.retryAfterMs = retryAfter;
    }

    get isClientError() { return this.status >= 400 && this.status < 500; }
    get isServerError() { return this.status >= 500; }
    get isRetryable()   { return this.isNetwork || this.isTimeout || this.isServerError; }

    // Which door closed. `null` when the backend did not say.
    get gate() { return this.detail?.gate ?? null; }

    // The row changed under the editor. The caller must NOT silently refetch and
    // resend: that is exactly the write this conflict exists to stop. It keeps
    // the local draft and lets a person decide.
    get isRevisionConflict() { return this.status === 409 && this.gate === 'revision'; }

    // The content is signed and cannot change by any door. Offering a save here
    // is offering a button that is guaranteed to fail.
    get isFrozen() { return this.status === 409 && this.gate === 'congelado'; }

    // The same idempotency key arrived with different content. Never retry this
    // one as-is — it means the key was reused for something it did not cover.
    get isIdempotencyConflict() { return this.status === 409 && this.gate === 'idempotencia'; }

    // A write whose outcome nobody knows: the request left, no answer came back.
    // Resending the SAME body with the SAME request_id is safe and is the only
    // correct move; generating a fresh key would duplicate the effect.
    get isUnconfirmed() { return this.isNetwork || this.isTimeout || this.isServerError; }

    static fromAxios(error) {
        if (error instanceof ApiError) return error;
        if (axios.isCancel(error)) {
            return new ApiError({
                message: 'Solicitud cancelada',
                code: 'CANCELLED',
                cause: error,
            });
        }
        if (error.code === 'ECONNABORTED') {
            return new ApiError({
                message: 'Tiempo de espera agotado',
                isTimeout: true,
                cause: error,
            });
        }
        if (!error.response) {
            return new ApiError({
                message: 'Error de red',
                isNetwork: true,
                cause: error,
            });
        }
        const { status, data, headers } = error.response;
        const retryAfter = status === 429 ? retryAfterMs(headers?.['retry-after']) : 0;
        return new ApiError({
            status,
            message: status === 429 ? rateLimitMessage(retryAfter) : readMessage(data, status),
            retryAfter,
            fields:  data?.fields || null,
            code:    data?.code   || null,
            detail:  readDetail(data),
            cause:   error,
        });
    }
}
